import React, { useContext, useState } from "react";
import { ProcessContext } from "../../../../../../contexts/ProcessContext";
import { ONBOARDING_FULFILLED_BY_TYPES } from "../../../../../shared/config";
import { UserDetailsWithKycProviderDataMapper } from "../../../../../../utils/mappers/MainUserDetails/MainUserDetailsMapper";
import { FLOW_MESSAGES } from "../../../../../shared/components/flowMessages/components";
import Text from "../../../../../shared/components/Text";
import {
  docIdTypes,
  documentTypes,
  maritalRegimeTypesConfig,
} from "../../../shared/config";
import { removeNullKeysFromObject } from "../constants/removeNullKeysFromObjects";
import { useActionCallback } from "../../../../../../utils/hooks/useActionCallback";

const initialState = {
  electronicIDPassed: false,
  electronicIdValues: null,
};

export const useKycAutomatic = ({ disableMaritalRegime }) => {
  const {
    state: { loan, participant },
    dispatch,
  } = useContext(ProcessContext);

  const [state, setState] = useState(initialState);
  const { electronicIDPassed, electronicIdValues } = state;

  const selectedDocumentType = documentTypes.find(
    (document) => document.display === electronicIdValues?.selectedDocumentType
  )?.id;

  const nieEu = selectedDocumentType === docIdTypes.NIE_UE;

  const participantProfessionalDetails =
    participant.onboardingDetails?.user_details?.professional_details;

  const onSubmitModule = (formValues) => {
    const newValues = {
      ...formValues,
      address: { ...formValues.address, ...formValues.complementAddress },
    };

    const valuesWithDefaultMaritalRegime = {
      ...newValues,
      maritalRegime: maritalRegimeTypesConfig.SEPARATE_PROPERTY.value,
    };

    const valuesByMaritalRegimeCustomParams =
      disableMaritalRegime === true
        ? valuesWithDefaultMaritalRegime
        : newValues;

    const isFulfilledByClientSideKycSignature =
      participant.onboardingFulfilledBy ===
      ONBOARDING_FULFILLED_BY_TYPES.CLIENT_SIDE_KYC_SIGNATURE;

    const requestData = {
      ...valuesByMaritalRegimeCustomParams,
      ...electronicIdValues,
      loan,
      documentType: selectedDocumentType,
    };

    const borrowerDocumentation = newValues?.passportFront
      ? [{ type: "passport", data: newValues.passportFront.result }]
      : undefined;

    let dehydratedData = isFulfilledByClientSideKycSignature
      ? UserDetailsWithKycProviderDataMapper.reverseMapByLoanValues(
          requestData,
          participant,
          borrowerDocumentation
        )
      : UserDetailsWithKycProviderDataMapper.reverseMapByFormValues(
          requestData,
          participant
        );

    dispatch({
      type: "REQUEST_PATCH_SET_USER_DETAILS_WITH_KYC_PROVIDER_DATA",
      payload: dehydratedData,
    });

    dispatch({
      type: "SET_FLOW_LOADING",
      payload: {
        show: true,
        loadingMessage: <Text tId="PLEASE_WAIT" />,
      },
    });
  };

  const onRetryElectronicId = () => {
    setState(initialState);
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: false,
    });
  };

  const onKoStep = ({ selectedDocumentType }) => {
    const documentType = documentTypes.find(
      (document) => document.display === selectedDocumentType
    )?.id;
    dispatch({
      type: "UPDATE_PROCESS_CONTEXT",
      payload: { documentType: documentType },
    });
    dispatch({
      type: "SET_MODULE_KO",
    });
  };

  const onElectronicIDOk = (electronicIdValues) => {
    setState((s) => ({
      ...s,
      electronicIDPassed: true,
      electronicIdValues,
    }));
  };

  const dispatchModuleOkRequests = () => {
    dispatch({
      type: "SET_FLOW_ITEM_OK",
    });
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: false },
    });
    dispatch({
      type: "SET_FLOW_MESSAGE",
      payload: { id: FLOW_MESSAGES.SEND_OK.id, fadeIn: false },
    });
  };

  useActionCallback(
    "REQUEST_PATCH_SET_USER_DETAILS_WITH_KYC_PROVIDER_DATA_FULFILLED",
    () => {
      if (participantProfessionalDetails) {
        const formattedCompanyInfo = participantProfessionalDetails.company_info
          ? removeNullKeysFromObject(
              participantProfessionalDetails.company_info
            )
          : null;

        dispatch({
          type: "REQUEST_PATCH_SET_PROFESSIONAL_DETAILS_AND_SEQUENCE",
          payload: {
            op: "processPatchSetProfessionalDetails",
            id: participant.participantId,
            loan_id: loan.id,
            professional_details: {
              ...participantProfessionalDetails,
              company_info: formattedCompanyInfo,
            },
          },
        });
      }

      if (!participantProfessionalDetails) {
        dispatchModuleOkRequests();
      }
    }
  );

  useActionCallback(
    "REQUEST_PATCH_SET_PROFESSIONAL_DETAILS_AND_SEQUENCE_FULFILLED",
    () => {
      dispatchModuleOkRequests();
    }
  );

  return {
    electronicIDPassed,
    nieEu,
    onSubmitModule,
    onKoStep,
    onElectronicIDOk,
    participant,
    onRetryElectronicId,
  };
};
